/**
 * @fileoverview Agent definition for quick-image-v2.
 *
 * This is a COMPOSITE agent that orchestrates a two-step pipeline for
 * zero-interaction image generation from selected prose:
 * 1. story-image-prompt-creator-v2: Generates a rich artistic prompt from prose
 * 2. story-image-creator-fal-v2: Generates an image from the prompt
 *
 * Model Configuration:
 * - This composite does NOT hardcode models in the flow
 * - Models are resolved via the standard priority chain:
 *   1. User's orchestration settings (Settings > Orchestration > quick-image-v2)
 *   2. User's primitive agent settings
 *   3. Primitive agent defaults
 * - The stepConfigs in metadata document RECOMMENDED fast models for reference
 *
 * @category visuals.images.story
 * @type composite
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas';
import { InputSchema, OutputSchema } from './schema';
// =============================================================================
// AGENT DEFINITION
// =============================================================================

export const definition: AppAgentDefinition = {
  id: 'quick-image-v2',
  kind: 'composite',
  name: 'Quick Image',
  description: 'Generates an image from selected prose with zero user interaction. Orchestrates prompt creation and image generation in a single pipeline.',
  category: 'visuals',
  inputSchema: InputSchema,
  outputSchema: OutputSchema,

  // --- Metadata ---
  metadata: {
    status: 'active',
    type: 'composite',
    addedIn: '2026-01',
    lastUpdated: '2026-01',
    taxonomyPath: 'visuals.images.story',
    usedBy: [
      'use-quick-image-v2.ts',
      'quick-image-lightbox.tsx',
    ],
    // Composite agents specify which primitives they orchestrate
    composedOf: [
      'story-image-prompt-creator-v2',
      'story-image-creator-fal-v2',
    ],
    // Recommended step configurations for "fast" mode
    // These are NOT enforced by the flow - they serve as documentation
    // and can be shown in the UI for users to configure in Orchestration settings
    stepConfigs: {
      'story-image-prompt-creator-v2': {
        modelProvider: 'xai',
        modelName: 'grok-4-1-fast-reasoning',
      },
      'story-image-creator-fal-v2': {
        modelProvider: 'fal-ai',
        modelName: 'fal-ai/z-image/turbo',
      },
    },
    relatedAgents: [
      'story-image-prompt-creator-v2',
      'story-image-creator-fal-v2',
      'multi-reference-visual-prompt-creator-v2',
    ],
  },
};
